"use client";

import { useQuery } from "@tanstack/react-query";
import { Button } from "design-system/button";
import { Dialog, DialogContent, DialogDescription } from "design-system/dialog";
import dynamic from "next/dynamic";
import { useState } from "react";
import { getTurnkeyRecoverySessionProfileId } from "@/config/turnkey";
import { getV3TurnkeySuborgId } from "@/gen/orval/webplatform-v3/endpoints/embedded-wallets/embedded-wallets.gen";
import { useCanCreateEmbeddedWallet } from "@/lib/hooks/flags/use-can-create-embedded-wallet";
import type { UseDialogReturn } from "@/lib/hooks/use-dialog";
import { useUser } from "@/lib/integrations/descope";
import { AuthErrorView } from "@/subsystems/access/turnkey/components/views/auth-error-view";
import { LoadingTurnkeyView } from "@/subsystems/access/turnkey/components/views/loading-turnkey-view";
import { StepHeader } from "@/subsystems/access/turnkey/components/views/step-header";

// The flow pulls in the Turnkey SDK; load it only when the dialog first opens, like the auth dialog.
const RecoverPasskeyFlow = dynamic(
  () =>
    import("@/subsystems/access/turnkey/components/flows/recover-passkey-flow").then((mod) => mod.RecoverPasskeyFlow),
  { loading: () => <LoadingTurnkeyView />, ssr: false },
);

// "Reset passkey" from Profile is the lost-passkey recovery started deliberately: email code (and SMS code when
// SMS recovery is on), a new passkey on this device, sign-in with it, and the old passkeys removed. The flow is
// the one behind "Lost your passkey?" on sign-in; this dialog only supplies what sign-in already had (email and
// sub-org) and a terminal, since nothing here waits on a session to flip.
export function ResetPasskeyDialog({ dialogControls }: { dialogControls: UseDialogReturn<void> }) {
  const enabled = useCanCreateEmbeddedWallet();
  // Never unmount an open dialog on a transient flag read; see CreateEmbeddedWalletDialog.
  if (!enabled && !dialogControls.props.open) return null;

  return (
    <Dialog {...dialogControls.props}>
      <DialogContent className="sm:max-w-xl">
        {dialogControls.props.open ? <ResetPasskeyBody onClose={dialogControls.dismiss} /> : null}
      </DialogContent>
    </Dialog>
  );
}

function ResetPasskeyBody({ onClose }: { onClose: () => void }) {
  const { user } = useUser();
  const email = user?.email ?? "";
  const sessionProfileId = getTurnkeyRecoverySessionProfileId();
  const [recovered, setRecovered] = useState(false);
  const lookup = useQuery({
    enabled: Boolean(email),
    queryFn: () => getV3TurnkeySuborgId(),
    queryKey: ["turnkey-reset-suborg-lookup", email],
    staleTime: 30_000,
  });

  if (recovered) {
    return (
      <>
        <StepHeader title="Passkey reset" />
        <DialogDescription className="text-neutral-600" data-testid="passkey-reset-done">
          Your new passkey is saved on this device and the old ones have been removed. Use it the next time you sign in.
        </DialogDescription>
        <Button className="w-full" data-testid="close-passkey-reset-btn" onClick={onClose} type="button">
          Done
        </Button>
      </>
    );
  }

  if (!email || !sessionProfileId || lookup.isError || (lookup.data && !lookup.data.subOrgId)) {
    return (
      <AuthErrorView
        actions={
          <Button className="w-full" data-testid="close-passkey-reset-btn" onClick={onClose} type="button">
            Close
          </Button>
        }
        description="We couldn't start a passkey reset for this wallet. Refresh the page and try again, or contact support if it keeps happening."
        testId="passkey-reset-error"
        title="Couldn't start the reset"
      />
    );
  }

  if (!lookup.data?.subOrgId) return <LoadingTurnkeyView />;

  return (
    <RecoverPasskeyFlow
      email={email}
      entry="profile"
      onBack={onClose}
      onRecovered={() => setRecovered(true)}
      sessionProfileId={sessionProfileId}
      subOrgId={lookup.data.subOrgId}
    />
  );
}
